/* Progressive commerce: native Shopify forms remain usable without this file.
 * Hooks: form[data-product-form] (id, quantity), [data-cart-open], [data-cart-count].
 * Cart writes are serialized and render Shopify's returned sections; no client price math.
 */
(() => {
  'use strict';
  const messages = JSON.parse(document.getElementById('StudioCommerceStrings')?.textContent || '{}');
  const rootPath = window.Shopify?.routes?.root || '/';
  const route = (path) => `${rootPath}${path}`;
  let mutating = false;
  let revision = 0;
  let drawerOpener;
  const variantCache = new WeakMap();
  const busyControls = new Map();
  const stickyRoots = new Set();
  let stickyFrame = 0;
  const drawer = () => document.querySelector('[data-cart-dialog]');
  const sectionIds = () => ['studio-cart-drawer', document.querySelector('[data-cart-page]')?.dataset.cartSectionId].filter(Boolean);

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : messages.cartError);
      return data;
    } finally { window.clearTimeout(timeout); }
  }

  function showError(scope, message, selector = '[data-product-error]') {
    let node = scope?.querySelector(selector);
    if (!node && scope) {
      node = document.createElement('p');
      node.className = 'commerce-error';
      node.setAttribute('role', 'alert');
      node.setAttribute(selector.slice(1, -1), '');
      scope.append(node);
    }
    if (node) { node.textContent = message; node.hidden = !message; }
  }

  function setCartBusy(busy) {
    document.querySelectorAll('[data-cart-content]').forEach((content) => {
      content.setAttribute('aria-busy', String(busy));
    });
    if (busy) {
      document.querySelectorAll('[data-cart-content] [data-cart-quantity], [data-cart-content] [data-quantity-step], [data-cart-checkout], [data-add-button], [data-sticky-add]').forEach((control) => {
        if (!busyControls.has(control)) busyControls.set(control, control.disabled);
        control.disabled = true;
      });
    } else {
      busyControls.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
      busyControls.clear();
      document.querySelectorAll('[data-product-root]').forEach(syncPurchaseState);
    }
  }

  function syncCount(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(count); node.dataset.count = String(count); });
    document.dispatchEvent(new CustomEvent('studio:cart-updated', { detail: { itemCount: count } }));
  }

  function formatMoney(scope = document) {
    scope.querySelectorAll('[data-money-cents]').forEach((node) => {
      const cents = Number(node.dataset.moneyCents);
      if (!Number.isFinite(cents)) return;
      const currency = node.dataset.currency || window.Shopify?.currency?.active || 'TRY';
      const isTurkishCart = document.documentElement.lang.startsWith('tr') && currency === 'TRY' && node.closest('[data-cart-content]');
      node.textContent = isTurkishCart
        ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' TL'
        : new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency }).format(cents / 100);
    });
  }

  function renderSections(sections, count) {
    if (!sections) return false;
    let drawerRendered = false;
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      for (const selector of ['[data-cart-dialog]', '[data-cart-page]']) {
        const next = parsed.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) {
          current.innerHTML = next.innerHTML;
          if (selector === '[data-cart-dialog]') drawerRendered = true;
          const serverCount = Number(next.querySelector('[data-item-count]')?.dataset.itemCount);
          if (Number.isFinite(serverCount)) count = serverCount;
        }
      }
    }
    formatMoney();
    const productIds = document.querySelector('[data-cart-product-ids]')?.dataset.cartProductIds?.split(',') || [];
    document.querySelectorAll('[data-product-root] [data-recommendation-product]').forEach((card) => { card.hidden = productIds.includes(card.dataset.recommendationProduct); });
    document.querySelectorAll('[data-product-root] [data-commerce-recommendations]').forEach((list) => { list.hidden = !list.querySelector('[data-recommendation-product]:not([hidden])'); });
    syncCount(count);
    return drawerRendered;
  }

  async function refreshCart() {
    const requestRevision = revision;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    url.searchParams.set('_cart', String(Date.now()));
    // Product routes negotiate a product JSON response for application/json, bypassing sections.
    const sections = await request(url.toString(), { cache: 'no-store', headers: { Accept: '*/*' } });
    if (requestRevision !== revision) return;
    if (!renderSections(sections)) {
      const cart = await request(route('cart.js'), { cache: 'no-store' });
      if (requestRevision === revision) syncCount(cart.item_count);
      throw new Error(messages.cartViewError);
    }
  }

  function openDrawer(opener) {
    const dialog = drawer();
    if (!dialog || typeof dialog.showModal !== 'function') { window.location.assign(route('cart')); return; }
    if (opener) drawerOpener = opener;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('commerce-drawer-open');
  }

  function closeDrawer() {
    drawer()?.close();
    document.body.classList.remove('commerce-drawer-open');
    if (drawerOpener?.isConnected) drawerOpener.focus({ preventScroll: true });
  }

  function recoverMessage(error) {
    return error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError
      ? messages.cartResponseError
      : error.message;
  }

  async function addProduct(form, submitter) {
    showError(form, '');
    if (mutating) { showError(form, messages.cartBusy); return; }
    const productRoot = form.closest('[data-product-root]');
    if (productRoot && form === productRoot.querySelector('[data-product-form]') && !selectedVariant(productRoot)?.available) return;
    if (!form.reportValidity()) return;
    mutating = true;
    revision += 1;
    const button = form.querySelector('[data-add-button]') || form.querySelector('[type="submit"]');
    const label = button?.querySelector('[data-add-label]');
    const originalLabel = label?.textContent;
    if (button) button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = messages.adding;
    setCartBusy(true);
    try {
      const body = new FormData(form);
      body.set('sections', sectionIds().join(','));
      body.set('sections_url', window.location.pathname);
      const result = await request(route('cart/add.js'), { method: 'POST', body });
      if (!renderSections(result.sections, result.item_count)) {
        try { await refreshCart(); } catch { window.location.assign(route('cart')); return; }
      }
      openDrawer(submitter || button);
      drawer()?.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    } catch (error) {
      showError(form, recoverMessage(error));
      // A lost response is ambiguous. Only read back; never retry a cart write.
      try { await refreshCart(); } catch { /* Keep the original actionable error. */ }
    } finally {
      mutating = false;
      setCartBusy(false);
      if (button) button.removeAttribute('aria-busy');
      if (label) label.textContent = originalLabel;
      if (productRoot) syncPurchaseState(productRoot);
    }
  }

  async function changeQuantity(line, quantity, focusId) {
    if (mutating || !Number.isInteger(quantity) || quantity < 0) return;
    const key = line.dataset.cartKey;
    if (!key) return;
    mutating = true;
    revision += 1;
    const content = line.closest('[data-cart-content]');
    showError(content, '', '[data-cart-error]');
    setCartBusy(true);
    try {
      const result = await request(route('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      if (!renderSections(result.sections, result.item_count)) {
        try { await refreshCart(); } catch { window.location.assign(route('cart')); return; }
      }
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      if (drawer()?.open && !drawer().contains(document.activeElement)) drawer().querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    } catch (error) {
      try { await refreshCart(); } catch { /* Render the error in the existing cart if readback also fails. */ }
      const currentContent = drawer()?.open ? drawer().querySelector('[data-cart-content]') : document.querySelector('[data-cart-page] [data-cart-content]');
      showError(currentContent || content, recoverMessage(error), '[data-cart-error]');
    } finally { mutating = false; setCartBusy(false); }
  }

  function productData(productRoot) {
    if (!variantCache.has(productRoot)) {
      try { variantCache.set(productRoot, JSON.parse(productRoot.querySelector('[data-product-variants]').textContent)); }
      catch { variantCache.set(productRoot, null); }
    }
    return variantCache.get(productRoot);
  }

  function selectedVariant(productRoot) {
    const form = productRoot.querySelector('[data-product-form]');
    return form && productData(productRoot)?.variants.find((entry) => String(entry.id) === new FormData(form).get('id'));
  }

  function syncDiscountBadge(node, variant) {
    if (node) node.hidden = !(variant.compareAtPrice > variant.price);
  }

  function syncPurchaseState(productRoot) {
    const variant = selectedVariant(productRoot);
    if (!variant) return;
    const form = productRoot.querySelector('[data-product-form]');
    const quantity = form.querySelector('[data-product-quantity]');
    const unavailable = !variant.available || (quantity && !quantity.checkValidity());
    [form.querySelector('[data-add-button]'), productRoot.querySelector('[data-sticky-add]')].filter(Boolean).forEach((button) => {
      button.disabled = Boolean(unavailable || mutating);
      const label = button.querySelector('[data-add-label]');
      if (label) label.textContent = mutating ? messages.adding : variant.available ? messages.add : messages.soldOut;
    });
    const stock = productRoot.querySelector('[data-low-stock]');
    if (stock) {
      stock.hidden = !(variant.available && variant.tracked && variant.inventoryPolicy === 'deny' && Number.isInteger(variant.inventoryQuantity) && variant.inventoryQuantity >= 1 && variant.inventoryQuantity <= 5);
      stock.textContent = stock.hidden ? '' : stock.dataset.stockTemplate.replace('[count]', String(variant.inventoryQuantity));
    }
    const money = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency: productData(productRoot).currency || 'TRY' });
    const stickyPrice = productRoot.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = money.format(variant.price / 100);
    const stickyCompare = productRoot.querySelector('[data-sticky-compare]');
    if (stickyCompare) { stickyCompare.hidden = !(variant.compareAtPrice > variant.price); stickyCompare.textContent = money.format((variant.compareAtPrice || 0) / 100); }
    syncDiscountBadge(productRoot.querySelector('[data-sticky-discount]'), variant);
    const stickyQuantity = productRoot.querySelector('[data-sticky-quantity]');
    if (stickyQuantity) stickyQuantity.textContent = stickyQuantity.dataset.quantityTemplate.replace('[count]', quantity?.value || '1');
    const stickyVariant = productRoot.querySelector('[data-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = productData(productRoot).variants.length > 1 ? ` · ${variant.title}` : '';
  }

  function scheduleSticky() {
    if (stickyFrame || !stickyRoots.size) return;
    // Instant/hash jumps can move the row below→above without an intersection transition.
    stickyFrame = requestAnimationFrame(() => {
      stickyFrame = 0;
      stickyRoots.forEach((productRoot) => {
        if (!productRoot.isConnected) { stickyRoots.delete(productRoot); return; }
        const bar = productRoot.querySelector('[data-sticky-purchase]');
        const row = productRoot.querySelector('.product-buy-row');
        if (bar && row) bar.hidden = row.getBoundingClientRect().bottom >= 100;
      });
    });
  }

  function trackSticky(productRoot) {
    if (!productRoot.querySelector('[data-sticky-purchase]')) return;
    stickyRoots.add(productRoot);
    scheduleSticky();
  }

  function selectMedia(productRoot, id) {
    if (!id || !productRoot.querySelector(`[data-product-media="${id}"]`)) return;
    productRoot.querySelectorAll('[data-product-media]').forEach((frame) => {
      frame.hidden = frame.dataset.productMedia !== String(id);
      if (frame.hidden) frame.querySelectorAll('video').forEach((video) => video.pause());
    });
    productRoot.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id))));
  }

  function updateVariant(productRoot, updateUrl) {
    const data = productData(productRoot);
    const form = productRoot.querySelector('[data-product-form]');
    if (!data || !form) return;
    const id = new FormData(form).get('id');
    const variant = data.variants.find((entry) => String(entry.id) === id);
    if (!variant) return;
    const money = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency: data.currency || 'TRY' });
    const price = productRoot.querySelector('[data-product-price]');
    if (price) price.textContent = money.format(variant.price / 100);
    const sku = productRoot.querySelector('[data-product-sku]');
    if (sku && typeof variant.sku === 'string') { sku.textContent = variant.sku; sku.parentElement.hidden = !variant.sku; }
    const compare = productRoot.querySelector('[data-product-compare]');
    if (compare) { compare.hidden = !(variant.compareAtPrice > variant.price); compare.textContent = money.format((variant.compareAtPrice || 0) / 100); }
    syncDiscountBadge(productRoot.querySelector('[data-product-discount]'), variant);
    const title = productRoot.querySelector('[data-selected-variant]');
    if (title) title.textContent = variant.title;
    const scentTitle = productRoot.querySelector('[data-scent-title]');
    const scentNote = productRoot.querySelector('[data-scent-note]');
    if (scentTitle) scentTitle.textContent = variant.title;
    const routineImage = document.querySelector('[data-routine-perfume] img');
    const routineTitle = document.querySelector('[data-routine-current-title]');
    if (routineImage && variant.imageUrl) { routineImage.src = variant.imageUrl; routineImage.removeAttribute('srcset'); routineImage.alt = `${variant.title}${messages.perfumeSuffix}`; }
    if (routineTitle) routineTitle.textContent = `${variant.title}${messages.perfumeSuffix}`;
    const routinePrice = document.querySelector('[data-routine-current-price]');
    if (routinePrice) { routinePrice.textContent = money.format(variant.price / 100); routinePrice.dataset.moneyCents = String(variant.price); }
    const routineCompare = document.querySelector('[data-routine-current-compare]');
    if (routineCompare) { routineCompare.hidden = !(variant.compareAtPrice > variant.price); routineCompare.textContent = money.format((variant.compareAtPrice || 0) / 100); routineCompare.dataset.moneyCents = String(variant.compareAtPrice || 0); }
    syncDiscountBadge(document.querySelector('[data-routine-current-discount]'), variant);
    if (scentNote) scentNote.textContent = variant.scentNote || '';
    productRoot.querySelectorAll('[data-scent-pyramid]').forEach(panel => { panel.hidden = panel.dataset.scentPyramid !== variant.title; });
    productRoot.querySelectorAll('[data-media-variant]').forEach(thumb => { thumb.hidden = thumb.dataset.mediaVariant !== variant.title; }); 
    const availability = productRoot.querySelector('[data-product-availability]');
    if (availability) availability.textContent = variant.available ? messages.available : messages.unavailable;
    const quantity = form.querySelector('[data-product-quantity]');
    if (quantity) {
      const min = variant.min || 1;
      const step = variant.step || 1;
      quantity.min = String(min);
      quantity.step = String(step);
      if (variant.max > 0) quantity.max = String(variant.max); else quantity.removeAttribute('max');
      const previous = Number(quantity.value) || min;
      const next = Math.max(min, min + Math.floor((previous - min) / step) * step);
      const maximum = variant.max >= min ? min + Math.floor((variant.max - min) / step) * step : null;
      quantity.value = String(maximum === null ? next : Math.min(maximum, next));
    }
    syncPurchaseState(productRoot);
    trackSticky(productRoot);
    if (updateUrl) {
      selectMedia(productRoot, variant.mediaId);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      showError(form, '');
    }
  }

  // Own product submits before Shopify's form listener so one AJAX add emits one analytics event.
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[data-product-form]')) { event.preventDefault(); void addProduct(event.target, event.submitter); }
    if (event.target.matches('.cart-form') && mutating) event.preventDefault();
  }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault(); openDrawer(opener);
      if (!mutating) {
        const dialog = drawer();
        dialog?.setAttribute('aria-busy', 'true');
        void refreshCart().catch((error) => showError(dialog?.querySelector('[data-cart-content]'), error.message, '[data-cart-error]')).finally(() => {
          dialog?.removeAttribute('aria-busy');
          if (dialog?.open && !dialog.contains(document.activeElement)) dialog.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
        });
      }
      return;
    }
    if (event.target.closest('[data-cart-close]')) { closeDrawer(); return; }
    if (event.target === drawer()) {
      const bounds = drawer().getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
      return;
    }
    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail) {
      const scope = thumbnail.closest('[data-product-root]');
      const linkedVariant = productData(scope)?.variants.find(item => String(item.mediaId) === thumbnail.dataset.mediaTarget);
      const input = linkedVariant && scope.querySelector(`[data-variant-choice][value="${linkedVariant.id}"]`);
      if (input) { input.checked = true; updateVariant(scope, true); }
      else selectMedia(scope, thumbnail.dataset.mediaTarget);
      return;
    }
    const stepButton = event.target.closest('[data-quantity-step]');
    if (stepButton) {
      const input = stepButton.closest('[data-quantity-control]')?.querySelector('input');
      if (!input || input.disabled) return;
      const oldValue = input.value;
      if (Number(stepButton.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      if (input.value !== oldValue) input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); void changeQuantity(remove.closest('[data-cart-line]'), 0); }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-choice]')) updateVariant(event.target.closest('[data-product-root]'), true);
    if (event.target.matches('[data-product-quantity]')) syncPurchaseState(event.target.closest('[data-product-root]'));
    if (event.target.matches('[data-cart-quantity]')) {
      if (!event.target.reportValidity()) return;
      void changeQuantity(event.target.closest('[data-cart-line]'), Number(event.target.value), event.target.id);
    }
  });
  document.addEventListener('input', (event) => { if (event.target.matches('[data-product-quantity]')) syncPurchaseState(event.target.closest('[data-product-root]')); });

  // Native dialog supplies focus containment and Escape. Keep body scroll in sync.
  document.addEventListener('close', (event) => {
    if (event.target.matches('[data-cart-dialog]')) { document.body.classList.remove('commerce-drawer-open'); if (drawerOpener?.isConnected) drawerOpener.focus({ preventScroll: true }); }
  }, true);
  document.addEventListener('shopify:section:load', () => { formatMoney(); document.querySelectorAll('[data-product-root]').forEach((element) => updateVariant(element, false)); });
  ['scroll', 'resize', 'hashchange', 'pageshow'].forEach((name) => window.addEventListener(name, scheduleSticky, { passive: true }));
  formatMoney();
  document.querySelectorAll('[data-product-root]').forEach((element) => updateVariant(element, false));
})();
